import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { DURATION_FAST, EASE_OUT } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { useImageUrl } from '@/services/uploads';
import SearchableSelect, { SearchableSelectOption } from './SearchableSelect';

interface EntityFilterSelectProps {
  options: SearchableSelectOption[];
  /** Empty string means "nothing selected" (the dropdown is shown). */
  value: string;
  onChange: (value: string) => void;
  /** Label for the "clear / all" first option of the dropdown. */
  allLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  /** Forwarded to the dropdown trigger. */
  'data-testid'?: string;
  /** Applied to the clear (X) button on the selected-item card. */
  clearTestId?: string;
}

/**
 * The compact card shown once an item is picked: a small image (or initial
 * fallback) to the side, the label + optional sublabel, and an X button that
 * clears the selection. Image resolution lives here — `useImageUrl` is a hook,
 * so it must run for the single selected item rather than inside an option map.
 */
const SelectedEntityCard: React.FC<{
  option: SearchableSelectOption;
  onClear: () => void;
  clearTestId?: string;
}> = ({ option, onClear, clearTestId }) => {
  const [imageUrl] = useImageUrl(option.imageUrl);
  const initial = option.label.trim().charAt(0).toUpperCase() || '?';

  return (
    <div className="flex items-center gap-3 w-full rounded-lg border border-[rgba(189,188,201,0.4)] bg-surface-elevated px-3 py-2">
      <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
        {imageUrl ? (
          <img src={imageUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="text-sm font-semibold text-primary-700 dark:text-primary-200">
            {initial}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text-primary">{option.label}</p>
        {option.sublabel && (
          <p className="truncate text-xs text-text-tertiary">{option.sublabel}</p>
        )}
      </div>

      <button
        type="button"
        onClick={onClear}
        aria-label="Clear selection"
        data-testid={clearTestId}
        className="shrink-0 rounded-full p-1 text-text-tertiary transition-colors duration-[120ms] hover:bg-action-hover hover:text-text-secondary active:scale-[0.97]"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

/**
 * A filter control that swaps between a `SearchableSelect` dropdown (when
 * nothing is selected) and a compact card of the chosen item (when one is).
 * The card's X button clears the filter and restores the dropdown. Behaviour
 * is identical to using `SearchableSelect` directly — this is purely a UX shell
 * shared by the analytics Course/Learner filters.
 */
const EntityFilterSelect: React.FC<EntityFilterSelectProps> = ({
  options,
  value,
  onChange,
  allLabel,
  loading = false,
  disabled = false,
  className = '',
  'data-testid': testId,
  clearTestId,
}) => {
  const selectedOption = value ? options.find((o) => o.value === value) ?? null : null;

  return (
    <div className={cn('relative', className)}>
      {/* `initial={false}` skips an entrance flash on first paint; `mode="wait"`
          keeps the two states from overlapping in the same layout slot. */}
      <AnimatePresence mode="wait" initial={false}>
        {selectedOption ? (
          <motion.div
            key="card"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: DURATION_FAST, ease: EASE_OUT }}
          >
            <SelectedEntityCard
              option={selectedOption}
              onClear={() => onChange('')}
              clearTestId={clearTestId}
            />
          </motion.div>
        ) : (
          <motion.div
            key="select"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: DURATION_FAST, ease: EASE_OUT }}
          >
            <SearchableSelect
              options={options}
              value=""
              onChange={onChange}
              allLabel={allLabel}
              loading={loading}
              disabled={disabled}
              data-testid={testId}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EntityFilterSelect;
